import { FC, useState } from 'react'
import {
  Text,
  GreyGrey30,
  GreyGrey70,
  GreyGrey80,
  Primary13Regular,
  Primary13SemiBold,
  Box,
  Flex
} from '@sequencehq/core-components'
import { Modal, TextFieldInput } from '@sequencehq/core-components'
import { ShortcutKey } from './ShortcutKey'
import { ShortcutGroup, leftColumnGroups, rightColumnGroups } from './shortcuts'

interface KeyboardShortcutsOverlayProps {
  isOpen: boolean
  onClose: () => void
}

interface ShortcutListProps {
  groups: ShortcutGroup[]
  searchQuery: string
}

const ShortcutList: FC<ShortcutListProps> = ({ groups, searchQuery }) => {
  const filteredGroups = groups
    .map(group => ({
      ...group,
      shortcuts: group.shortcuts.filter(shortcut =>
        shortcut.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }))
    .filter(group => group.shortcuts.length > 0)

  return (
    <Flex direction="column" gap={0} align="stretch">
      {filteredGroups.map((group, groupIndex) => (
        <Box key={group.title}>
          <Box height="40px">
            <Text {...Primary13SemiBold} color={GreyGrey80} pt={2}>
              {group.title}
            </Text>
          </Box>
          <Flex direction="column" gap={3} align="stretch">
            {group.shortcuts.map((shortcut, index) => (
              <Flex key={index} justify="space-between" gap={4} align="center">
                <Text {...Primary13Regular} color={GreyGrey70}>
                  {shortcut.description}
                </Text>
                <ShortcutKey
                  keys={shortcut.keys}
                  isSimultaneous={shortcut.isSimultaneous}
                />
              </Flex>
            ))}
          </Flex>
          {groupIndex < filteredGroups.length - 1 && <Box height="24px" />}
        </Box>
      ))}
    </Flex>
  )
}

export const KeyboardShortcutsOverlay: FC<KeyboardShortcutsOverlayProps> = ({
  isOpen,
  onClose
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="490px"
      data-testid="keyboardShortcutsOverlay"
    >
      <Modal.Header showCloseButton withBorder>
        Keyboard shortcuts
      </Modal.Header>
      <OverlayContent />
    </Modal>
  )
}

// Holds the search state inside the modal content, which Chakra unmounts on
// close — so the search resets each time the overlay opens.
const OverlayContent: FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const allGroups = [...leftColumnGroups, ...rightColumnGroups]

  return (
    <Box position="relative">
      <Modal.Content padding={0}>
        <Box px={4} pt={4}>
          <Box
            display="flex"
            flexDirection="column"
            overflow="hidden"
            height="579px"
          >
            <Box mb={4}>
              <TextFieldInput
                placeholder="Search keyboard shortcuts"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                height="32px"
              />
            </Box>
            <Box
              flex={1}
              overflowY="auto"
              marginRight="-24px"
              paddingRight="24px"
              paddingBottom="40px"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '4px'
                },
                '&::-webkit-scrollbar-track': {
                  width: '4px'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: GreyGrey30,
                  borderRadius: '24px'
                }
              }}
            >
              <ShortcutList groups={allGroups} searchQuery={searchQuery} />
            </Box>
          </Box>
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height="40px"
            pointerEvents="none"
            background="linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)"
            opacity={0.8}
          />
        </Box>
      </Modal.Content>
    </Box>
  )
}
